'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function PersonalLoanCalculator() {
  const t = useTranslations("tools.personalLoanCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const [inputs, setInputs] = useState({
    loanAmount: 20000,
    interestRate: 10,
    loanYears: 5,
    loanMonths: 0,
    startMonth: new Date().getMonth(),
    startYear: new Date().getFullYear(),
    includeFees: false,
    originationFee: 5,
    feeType: 'percentage',
    feePayment: 'deduct',
    insurance: 0,
  });

  const [results, setResults] = useState<any>(null);
  const [amortizationSchedule, setAmortizationSchedule] = useState<any[]>([]);
  const [displayedRows, setDisplayedRows] = useState(12);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const baseStyles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative' as const,
      zIndex: 1,
    },
    header: {
      textAlign: 'center' as const,
    },
    h1: {
      fontWeight: 700,
      color: colors.textPrimary,
      lineHeight: 1.2,
    },
    summaryText: {
      color: colors.textSecondary,
      lineHeight: 1.6,
    },
    calculatorTool: {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
    },
    toolContainer: {
      maxWidth: '100%',
      margin: '0 auto',
    },
    glassCard: {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
    },
    h2: {
      fontWeight: 600,
      color: colors.textPrimary,
    },
    paragraph: {
      lineHeight: 1.6,
      color: colors.textSecondary,
    },
    formGroup: {
      marginBottom: '1rem',
    },
    label: {
      display: 'block',
      fontWeight: 600,
      color: '#334155',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    select: {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
      background: 'white',
    },
    calculateButton: {
      width: '100%',
      fontWeight: 700,
      color: '#fff',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    resultsCard: {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    mainResult: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, #1d4ed8 100%)`,
      color: 'white',
      borderRadius: '0.75rem',
      textAlign: 'center' as const,
    },
    resultItem: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '1rem',
      borderBottom: '1px solid #e2e8f0',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
    },
    th: {
      background: '#f1f5f9',
      padding: '0.75rem',
      textAlign: 'left' as const,
      fontWeight: 600,
      color: '#475569',
      borderBottom: '1px solid #e2e8f0',
    },
    td: {
      padding: '0.75rem',
      color: '#334155',
      borderBottom: '1px solid #e2e8f0',
    },
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { loanAmount, interestRate, loanYears, loanMonths, startMonth, startYear, includeFees, originationFee, feeType, feePayment, insurance } = inputs;

    let actualOriginationFee = 0;
    let actualLoanAmount = loanAmount;

    if (includeFees) {
      if (feeType === 'percentage') {
        actualOriginationFee = loanAmount * (originationFee / 100);
      } else {
        actualOriginationFee = originationFee;
      }

      if (feePayment === 'deduct') {
        actualLoanAmount = loanAmount;
      }
    }

    const totalMonths = loanYears * 12 + loanMonths;

    if (totalMonths === 0) {
      alert(t('invalidTermError'));
      return;
    }

    const monthlyRate = interestRate / 100 / 12;
    let monthlyPayment: number;

    if (monthlyRate === 0) {
      monthlyPayment = actualLoanAmount / totalMonths;
    } else {
      monthlyPayment = actualLoanAmount * (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / (Math.pow(1 + monthlyRate, totalMonths) - 1);
    }

    monthlyPayment += insurance;

    const totalPayments = monthlyPayment * totalMonths;
    const totalInterest = totalPayments - actualLoanAmount - (insurance * totalMonths);

    const payoffDate = new Date(startYear, startMonth);
    payoffDate.setMonth(payoffDate.getMonth() + totalMonths);

    const principalPercent = (actualLoanAmount / totalPayments * 100).toFixed(1);
    const interestPercent = ((totalInterest + insurance * totalMonths) / totalPayments * 100).toFixed(1);

    const schedule = generateAmortizationSchedule(actualLoanAmount, monthlyRate, monthlyPayment - insurance, totalMonths, startMonth, startYear, insurance);

    setResults({
      monthlyPayment: monthlyPayment.toFixed(2),
      totalPayments: totalPayments.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
      payoffDate: payoffDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      principalPercent: parseFloat(principalPercent),
      interestPercent: parseFloat(interestPercent),
      actualLoanAmount,
      cashReceived: includeFees && feePayment === 'deduct' ? (loanAmount - actualOriginationFee).toFixed(2) : loanAmount.toFixed(2),
    });

    setAmortizationSchedule(schedule);
    setDisplayedRows(12);
  };

  const generateAmortizationSchedule = (principal: number, monthlyRate: number, monthlyPayment: number, totalMonths: number, startMonth: number, startYear: number, insurance: number) => {
    const schedule = [];
    let balance = principal;
    const currentDate = new Date(startYear, startMonth);

    for (let i = 1; i <= totalMonths; i++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = monthlyPayment - interestPayment;
      balance -= principalPayment;

      if (balance < 0) balance = 0;

      currentDate.setMonth(currentDate.getMonth() + 1);

      schedule.push({
        month: i,
        date: new Date(currentDate),
        payment: monthlyPayment + insurance,
        principal: principalPayment,
        interest: interestPayment,
        balance: balance,
      });
    }

    return schedule;
  };

  const drawPieChart = () => {
    if (!results) return null;

    const { principalPercent, interestPercent } = results;
    const centerX = 100;
    const centerY = 100;
    const radius = 80;

    const principalAngle = (principalPercent / 100) * 360;
    const interestAngle = (interestPercent / 100) * 360;

    const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
      const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
      return {
        x: centerX + (radius * Math.cos(angleInRadians)),
        y: centerY + (radius * Math.sin(angleInRadians)),
      };
    };

    const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
      const start = polarToCartesian(x, y, radius, endAngle);
      const end = polarToCartesian(x, y, radius, startAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

      const d = [
        'M', x, y,
        'L', start.x, start.y,
        'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y,
        'Z',
      ].join(' ');

      return d;
    };

    const principalPath = describeArc(centerX, centerY, radius, 0, principalAngle);
    const interestPath = describeArc(centerX, centerY, radius, principalAngle, principalAngle + interestAngle);

    return (
      <svg width="200" height="200" style={{ display: 'block', margin: '0 auto' }}>
        <path d={principalPath} fill="#2563eb" />
        <path d={interestPath} fill="#f59e0b" />
      </svg>
    );
  };

  const containerStyle = getResponsiveStyle(
    baseStyles.container,
    { padding: '1rem' },
    { padding: '2rem' },
    { padding: '3rem' }
  );

  const headerStyle = getResponsiveStyle(
    baseStyles.header,
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    baseStyles.h1,
    { fontSize: '2rem', marginBottom: '1rem' },
    { fontSize: '2.5rem', marginBottom: '1.25rem' },
    { fontSize: '3rem', marginBottom: '1.5rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    baseStyles.summaryText,
    { fontSize: '1rem', marginBottom: '0' },
    { fontSize: '1.125rem', marginBottom: '0' },
    { fontSize: '1.25rem', marginBottom: '0' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    baseStyles.calculatorTool,
    { padding: '1.5rem 1rem', marginBottom: '2rem' },
    { padding: '2rem 1.5rem', marginBottom: '2.5rem' },
    { padding: '3rem 2rem', marginBottom: '3rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    baseStyles.toolContainer,
    { maxWidth: '100%' },
    { maxWidth: '600px' },
    { maxWidth: '1000px' }
  );

  const glassCardStyle = getResponsiveStyle(
    baseStyles.glassCard,
    { padding: '1.5rem', marginBottom: '1.5rem' },
    { padding: '2rem', marginBottom: '2rem' },
    { padding: '2.5rem', marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    baseStyles.h2,
    { fontSize: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    baseStyles.paragraph,
    { fontSize: '1rem', marginBottom: '1rem' },
    { fontSize: '1.125rem', marginBottom: '1.25rem' },
    { fontSize: '1.25rem', marginBottom: '1.5rem' }
  );

  const inputStyle = getResponsiveStyle(
    baseStyles.input,
    { padding: '0.75rem' },
    { padding: '0.875rem 1rem' },
    { padding: '1rem 1.25rem' }
  );

  const selectStyle = getResponsiveStyle(
    baseStyles.select,
    { padding: '0.75rem' },
    { padding: '0.875rem 1rem' },
    { padding: '1rem 1.25rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    baseStyles.calculateButton,
    { padding: '0.875rem 1.5rem', fontSize: '1rem', marginTop: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem', marginTop: '1.25rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.25rem', marginTop: '1.5rem' }
  );

  const resultsCardStyle = getResponsiveStyle(
    baseStyles.resultsCard,
    { padding: '1.5rem', marginTop: '1.5rem' },
    { padding: '2rem', marginTop: '2rem' },
    { padding: '2.5rem', marginTop: '2.5rem' }
  );

  const mainResultStyle = getResponsiveStyle(
    baseStyles.mainResult,
    { padding: '1.5rem', marginBottom: '1.5rem' },
    { padding: '1.75rem', marginBottom: '1.75rem' },
    { padding: '2rem', marginBottom: '2rem' }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <h2 style={{ ...h2Style, textAlign: 'center', marginBottom: '1.5rem' }}>{t('toolTitle')}</h2>

          <div style={{ display: 'grid', gridTemplateColumns: screenSize === 'mobile' ? '1fr' : '1fr 1fr', gap: '2rem' }}>
            <div style={{ background: 'white', padding: screenSize === 'mobile' ? '1.5rem' : '2rem', borderRadius: '1rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
              <form onSubmit={handleCalculate}>
                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label}>{t('loanAmountLabel')}</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="number"
                      value={inputs.loanAmount}
                      onChange={(e) => setInputs({ ...inputs, loanAmount: parseFloat(e.target.value) || 0 })}
                      style={inputStyle}
                      min="0"
                      step="100"
                      required
                    />
                    <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: 500, pointerEvents: 'none' }}>$</span>
                  </div>
                </div>

                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label}>{t('interestRateLabel')}</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="number"
                      value={inputs.interestRate}
                      onChange={(e) => setInputs({ ...inputs, interestRate: parseFloat(e.target.value) || 0 })}
                      style={inputStyle}
                      min="0"
                      max="100"
                      step="0.1"
                      required
                    />
                    <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: 500, pointerEvents: 'none' }}>%</span>
                  </div>
                </div>

                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label}>{t('loanTermLabel')}</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="number"
                        value={inputs.loanYears}
                        onChange={(e) => setInputs({ ...inputs, loanYears: parseInt(e.target.value) || 0 })}
                        style={inputStyle}
                        min="0"
                        max="30"
                      />
                      <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: 500, pointerEvents: 'none', fontSize: '0.875rem' }}>{t('yearsLabel')}</span>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="number"
                        value={inputs.loanMonths}
                        onChange={(e) => setInputs({ ...inputs, loanMonths: parseInt(e.target.value) || 0 })}
                        style={inputStyle}
                        min="0"
                        max="11"
                      />
                      <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: 500, pointerEvents: 'none', fontSize: '0.875rem' }}>{t('monthsLabel')}</span>
                    </div>
                  </div>
                </div>

                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label}>{t('startDateLabel')}</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <select
                      value={inputs.startMonth}
                      onChange={(e) => setInputs({ ...inputs, startMonth: parseInt(e.target.value) })}
                      style={selectStyle}
                    >
                      <option value="0">{t('january')}</option>
                      <option value="1">{t('february')}</option>
                      <option value="2">{t('march')}</option>
                      <option value="3">{t('april')}</option>
                      <option value="4">{t('may')}</option>
                      <option value="5">{t('june')}</option>
                      <option value="6">{t('july')}</option>
                      <option value="7">{t('august')}</option>
                      <option value="8">{t('september')}</option>
                      <option value="9">{t('october')}</option>
                      <option value="10">{t('november')}</option>
                      <option value="11">{t('december')}</option>
                    </select>
                    <input
                      type="number"
                      value={inputs.startYear}
                      onChange={(e) => setInputs({ ...inputs, startYear: parseInt(e.target.value) || new Date().getFullYear() })}
                      style={inputStyle}
                      min="2020"
                      max="2050"
                    />
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '2px solid #e2e8f0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 600, color: '#334155', marginBottom: '1rem' }}>
                    <input
                      type="checkbox"
                      checked={inputs.includeFees}
                      onChange={(e) => setInputs({ ...inputs, includeFees: e.target.checked })}
                      style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                    />
                    {t('includeFeesLabel')}
                  </label>

                  {inputs.includeFees && (
                    <div style={{ paddingLeft: '1.5rem', marginTop: '1rem' }}>
                      <div style={baseStyles.formGroup}>
                        <label style={baseStyles.label}>{t('originationFeeLabel')}</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="number"
                              value={inputs.originationFee}
                              onChange={(e) => setInputs({ ...inputs, originationFee: parseFloat(e.target.value) || 0 })}
                              style={inputStyle}
                              min="0"
                              step="0.1"
                            />
                            <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: 500, pointerEvents: 'none' }}>
                              {inputs.feeType === 'percentage' ? '%' : '$'}
                            </span>
                          </div>
                          <select
                            value={inputs.feeType}
                            onChange={(e) => setInputs({ ...inputs, feeType: e.target.value })}
                            style={selectStyle}
                          >
                            <option value="percentage">{t('percentageOption')}</option>
                            <option value="amount">{t('dollarAmountOption')}</option>
                          </select>
                        </div>
                      </div>

                      <div style={baseStyles.formGroup}>
                        <label style={baseStyles.label}>{t('feePaymentLabel')}</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#475569' }}>
                            <input
                              type="radio"
                              name="feePayment"
                              value="deduct"
                              checked={inputs.feePayment === 'deduct'}
                              onChange={(e) => setInputs({ ...inputs, feePayment: e.target.value })}
                              style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                            />
                            {t('deductedFromLoan')}
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#475569' }}>
                            <input
                              type="radio"
                              name="feePayment"
                              value="upfront"
                              checked={inputs.feePayment === 'upfront'}
                              onChange={(e) => setInputs({ ...inputs, feePayment: e.target.value })}
                              style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                            />
                            {t('paidUpfront')}
                          </label>
                        </div>
                      </div>

                      <div style={baseStyles.formGroup}>
                        <label style={baseStyles.label}>{t('monthlyInsuranceLabel')}</label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="number"
                            value={inputs.insurance}
                            onChange={(e) => setInputs({ ...inputs, insurance: parseFloat(e.target.value) || 0 })}
                            style={inputStyle}
                            min="0"
                            step="1"
                          />
                          <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: 500, pointerEvents: 'none' }}>$</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <button type="submit" style={calculateButtonStyle}>
                  {t('calculateButton')}
                </button>
              </form>
            </div>

            {results && (
              <div style={resultsCardStyle}>
                <h3 style={{ ...h2Style, textAlign: 'center' }}>{t('loanSummaryTitle')}</h3>

                <div style={mainResultStyle}>
                  <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '0.5rem' }}>{t('monthlyPaymentLabel')}</div>
                  <div style={{ fontSize: screenSize === 'mobile' ? '2rem' : '2.5rem', fontWeight: 700 }}>${results.monthlyPayment}</div>
                </div>

                {inputs.includeFees && inputs.feePayment === 'deduct' && (
                  <div style={baseStyles.resultItem}>
                    <span style={{ color: '#64748b', fontWeight: 500 }}>{t('cashReceivedLabel')}</span>
                    <span style={{ color: colors.textPrimary, fontWeight: 700, fontSize: '1.1rem' }}>${results.cashReceived}</span>
                  </div>
                )}

                <div style={baseStyles.resultItem}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t('totalPaymentsLabel')}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 700, fontSize: '1.1rem' }}>${results.totalPayments}</span>
                </div>

                <div style={baseStyles.resultItem}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t('totalInterestLabel')}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 700, fontSize: '1.1rem' }}>${results.totalInterest}</span>
                </div>

                <div style={{ ...baseStyles.resultItem, borderBottom: 'none' }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>{t('payoffDateLabel')}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 700, fontSize: '1.1rem' }}>{results.payoffDate}</span>
                </div>

                <div style={{ margin: '2rem 0', textAlign: 'center' }}>
                  {drawPieChart()}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: '20px', height: '20px', borderRadius: '0.25rem', background: '#2563eb' }}></div>
                      <span>{t('principalLabel')} ({results.principalPercent}%)</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: '20px', height: '20px', borderRadius: '0.25rem', background: '#f59e0b' }}></div>
                      <span>{t('interestLabel')} ({results.interestPercent}%)</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {results && amortizationSchedule.length > 0 && (
            <div style={{ marginTop: '2rem', background: 'white', padding: screenSize === 'mobile' ? '1.5rem' : '2rem', borderRadius: '1rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
              <h3 style={h2Style}>{t('amortizationScheduleTitle')}</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={baseStyles.table}>
                  <thead>
                    <tr>
                      <th style={baseStyles.th}>{t('monthColumn')}</th>
                      <th style={baseStyles.th}>{t('paymentColumn')}</th>
                      <th style={baseStyles.th}>{t('principalColumn')}</th>
                      <th style={baseStyles.th}>{t('interestColumn')}</th>
                      <th style={baseStyles.th}>{t('balanceColumn')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {amortizationSchedule.slice(0, displayedRows).map((row, index) => (
                      <tr key={index} style={{ background: index % 2 === 0 ? 'transparent' : '#f8fafc' }}>
                        <td style={baseStyles.td}>{row.date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</td>
                        <td style={baseStyles.td}>${row.payment.toFixed(2)}</td>
                        <td style={baseStyles.td}>${row.principal.toFixed(2)}</td>
                        <td style={baseStyles.td}>${row.interest.toFixed(2)}</td>
                        <td style={baseStyles.td}>${row.balance.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {displayedRows < amortizationSchedule.length && (
                <button
                  onClick={() => setDisplayedRows(displayedRows + 12)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#f1f5f9',
                    color: '#475569',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    marginTop: '1rem',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#e2e8f0'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#f1f5f9'}
                >
                  {t('showMoreButton')}
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={{ paddingLeft: '1.5rem', color: colors.textSecondary, lineHeight: 1.8 }}>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('step4Title')}</strong> {t('step4Description')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('step5Title')}</strong> {t('step5Description')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('step6Title')}</strong> {t('step6Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <div style={{ background: 'rgba(255, 90, 112, 0.05)', padding: '1.5rem', borderRadius: '0.75rem', marginBottom: '1.5rem', borderLeft: '4px solid ' + colors.primary }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem' }}>{t('currentRatesTitle')}</h3>
          <p style={paragraphStyle}>{t('currentRatesDescription')}</p>
        </div>
        <div style={{ background: 'rgba(255, 90, 112, 0.05)', padding: '1.5rem', borderRadius: '0.75rem', marginBottom: '1.5rem', borderLeft: '4px solid ' + colors.primary }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem' }}>{t('aprVsRateTitle')}</h3>
          <p style={paragraphStyle}>{t('aprVsRateDescription')}</p>
        </div>
        <div style={{ background: 'rgba(255, 90, 112, 0.05)', padding: '1.5rem', borderRadius: '0.75rem', marginBottom: '1.5rem', borderLeft: '4px solid ' + colors.primary }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem' }}>{t('termImpactTitle')}</h3>
          <p style={paragraphStyle}>{t('termImpactDescription')}</p>
        </div>
        <div style={{ background: 'rgba(255, 90, 112, 0.05)', padding: '1.5rem', borderRadius: '0.75rem', borderLeft: '4px solid ' + colors.primary }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem' }}>{t('creditImpactTitle')}</h3>
          <p style={paragraphStyle}>{t('creditImpactDescription')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ fontSize: '1.5rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' }}>{t('understandingAPRTitle')}</h3>
        <p style={paragraphStyle}>{t('understandingAPRDescription')}</p>

        <h3 style={{ fontSize: '1.5rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: '2rem' }}>{t('originationFeesTitle')}</h3>
        <p style={paragraphStyle}>{t('originationFeesDescription')}</p>

        <h3 style={{ fontSize: '1.5rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: '2rem' }}>{t('amortizationExplainedTitle')}</h3>
        <p style={paragraphStyle}>{t('amortizationExplainedDescription')}</p>

        <h3 style={{ fontSize: '1.5rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: '2rem' }}>{t('debtConsolidationTitle')}</h3>
        <p style={paragraphStyle}>{t('debtConsolidationDescription')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' }}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' }}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' }}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' }}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' }}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
        <div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' }}>{t('faq6Question')}</h3>
          <p style={paragraphStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={{ paddingLeft: '1.5rem', color: colors.textSecondary, lineHeight: 1.8 }}>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.citi.com/personal-loans/learning-center/calculators/personal-loan-calculator" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t('reference1')}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://smartasset.com/personal-loans/personal-loan-calculator" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t('reference2')}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.sofi.com/personal-loan-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t('reference3')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}